import { Activity, Clock, Package, Server, TrendingUp, Users, Zap } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { StatusBadge } from "../../components/ui/status-badge";
import { adminApi, AdminDeployment } from "../../lib/adminApi";

function timeAgo(dateStr?: string): string {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function fmtDate(d: string) {
  const dt = new Date(d);
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  loading,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ComponentType<{ className?: string }>;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
            {loading ? (
              <Skeleton className="mt-1.5 h-8 w-16" />
            ) : (
              <p className="mt-1 text-h1">{value}</p>
            )}
            {sub && !loading && (
              <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>
            )}
          </div>
          <div className="rounded-lg bg-muted p-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const CHART_COLORS = {
  success: "hsl(142, 71%, 45%)",
  failed: "hsl(0, 84%, 60%)",
  total: "hsl(217, 91%, 60%)",
  users: "hsl(262, 83%, 58%)",
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [queue, setQueue] = useState<AdminDeployment[]>([]);
  const [recentDeployments, setRecentDeployments] = useState<AdminDeployment[]>([]);
  const [trends, setTrends] = useState<{
    deployments: { date: string; total: number; success: number; failed: number }[];
    users: { date: string; count: number }[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    Promise.all([
      adminApi.stats(),
      adminApi.buildQueue(),
      adminApi.listDeployments({ limit: 15, sort: "createdAt", order: "desc", enrich: true }),
    ]).then(([s, q, d]) => {
      setStats(s);
      setQueue(q.queue ?? []);
      setRecentDeployments(d.deployments ?? []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setTrendsLoading(true);
    adminApi.statsTrends(days)
      .then(setTrends)
      .catch(console.error)
      .finally(() => setTrendsLoading(false));
  }, [days]);

  // Compute totals from trend data for the bar chart y-axis
  const maxDep = trends
    ? Math.max(...trends.deployments.map((d) => d.total), 1)
    : 10;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 tracking-tight">Platform Overview</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Real-time summary of all platform activity.</p>
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Users"
          value={stats?.users?.total ?? "—"}
          sub={stats ? `+${stats.users.thisMonth} this month` : undefined}
          icon={Users}
          loading={loading}
        />
        <StatCard
          label="Projects"
          value={stats?.projects?.total ?? "—"}
          icon={Package}
          loading={loading}
        />
        <StatCard
          label="Services"
          value={stats?.services?.total ?? "—"}
          sub={stats ? `${stats.services.running} running · ${stats.services.failed} failed` : undefined}
          icon={Server}
          loading={loading}
        />
        <StatCard
          label="Deployments"
          value={stats?.deployments?.total ?? "—"}
          sub={stats ? `${stats.deployments.successRate}% success rate` : undefined}
          icon={Zap}
          loading={loading}
        />
      </div>

      {/* Services health strip */}
      {!loading && stats && (
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-success" />
                <span className="font-medium">{stats.services.running}</span>
                <span className="text-muted-foreground">running</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-warning animate-pulse" />
                <span className="font-medium">{stats.services.building}</span>
                <span className="text-muted-foreground">building</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-destructive" />
                <span className="font-medium">{stats.services.failed}</span>
                <span className="text-muted-foreground">failed</span>
              </div>
              <div className="ml-auto flex items-center gap-1.5 text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                <span>Avg build: {stats.deployments.avgBuildSeconds}s</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts row */}
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
          <TrendingUp className="h-3.5 w-3.5" />
          Activity trends
        </h2>
        <div className="flex gap-1">
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                days === d
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Deployment frequency chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Deployments per day</CardTitle>
          </CardHeader>
          <CardContent>
            {trendsLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={trends?.deployments ?? []}
                  margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
                  barGap={1}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={fmtDate}
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    tickLine={false}
                    axisLine={false}
                    interval={Math.floor((trends?.deployments.length ?? 30) / 5)}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, maxDep]}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "6px",
                      fontSize: 12,
                    }}
                    formatter={(val: any, name: any) => [val, String(name).charAt(0).toUpperCase() + String(name).slice(1)]}
                    labelFormatter={(label) => fmtDate(String(label))}
                  />
                  <Bar dataKey="success" stackId="a" fill={CHART_COLORS.success} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="failed" stackId="a" fill={CHART_COLORS.failed} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
            {!trendsLoading && (
              <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-sm" style={{ background: CHART_COLORS.success }} />
                  Success
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-sm" style={{ background: CHART_COLORS.failed }} />
                  Failed
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* New users chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">New user signups</CardTitle>
          </CardHeader>
          <CardContent>
            {trendsLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : trends?.users && trends.users.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart
                  data={trends.users}
                  margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.users} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={CHART_COLORS.users} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={fmtDate}
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "6px",
                      fontSize: 12,
                    }}
                    formatter={(val: any) => [val, "New users"]}
                    labelFormatter={(label) => fmtDate(String(label))}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={CHART_COLORS.users}
                    strokeWidth={2}
                    fill="url(#userGrad)"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
                No new signups in this period.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Build queue */}
      {queue.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="h-4 w-4 text-warning animate-pulse" />
              Build Queue ({queue.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="pb-2 text-left font-medium">Service</th>
                  <th className="pb-2 text-left font-medium">Stage</th>
                  <th className="pb-2 text-left font-medium">Started</th>
                  <th className="pb-2 text-right font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {queue.map((d) => (
                  <tr key={d._id ?? d.id} className="cursor-pointer hover:bg-muted/40">
                    <td className="py-2.5 pr-4 font-medium">{d.serviceName ?? d.serviceId ?? "—"}</td>
                    <td className="py-2.5 pr-4 text-muted-foreground capitalize">{d.stage ?? "—"}</td>
                    <td className="py-2.5 pr-4 text-muted-foreground">{timeAgo(d.createdAt)}</td>
                    <td className="py-2.5 text-right">
                      <StatusBadge kind="deployment" status={d.status ?? "building"} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Recent deployments */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap className="h-4 w-4 text-muted-foreground" />
            Recent Deployments
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : recentDeployments.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No deployments yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
                    <th className="px-4 py-2.5 text-left font-medium">Status</th>
                    <th className="px-4 py-2.5 text-left font-medium">Service / Project</th>
                    <th className="px-4 py-2.5 text-left font-medium">Triggered by</th>
                    <th className="px-4 py-2.5 text-left font-medium">Commit</th>
                    <th className="px-4 py-2.5 text-left font-medium">Branch</th>
                    <th className="px-4 py-2.5 text-left font-medium">Age</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {recentDeployments.map((d) => {
                    const depId = d._id ?? d.id ?? "";
                    const commit = d.commitHash ?? d.gitCommit ?? "";
                    const shortCommit = commit ? commit.slice(0, 7) : null;
                    return (
                      <tr
                        key={depId}
                        className="hover:bg-muted/40 transition-colors cursor-pointer"
                        onClick={() => {
                          if (d.projectId && depId) window.open(`/projects/${d.projectId}/deployments/${depId}`, "_blank");
                        }}
                      >
                        <td className="px-4 py-3">
                          <StatusBadge kind="deployment" status={d.status ?? "unknown"} />
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium leading-tight">{d.serviceName ?? d.serviceId ?? "—"}</p>
                          {d.projectName && (
                            <p className="text-xs text-muted-foreground mt-0.5">{d.projectName}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {d.triggeredByEmail
                            ? <span title={d.triggeredBy}>{d.triggeredByEmail}</span>
                            : d.trigger === "git-push"
                              ? <Badge variant="outline" className="text-xs">git-push</Badge>
                              : (d.trigger ? <Badge variant="outline" className="text-xs">{d.trigger}</Badge> : "—")}
                        </td>
                        <td className="px-4 py-3">
                          {shortCommit ? (
                            <div>
                              <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{shortCommit}</span>
                              {d.commitMessage && (
                                <p className="text-xs text-muted-foreground mt-0.5 max-w-xs truncate">{d.commitMessage}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{d.branch ?? "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{timeAgo(d.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
